import { Link } from "react-router-dom";
import { Droplets, Youtube, Instagram, Facebook, Linkedin, Twitter, Send, Phone, MessageCircle, Mail, MapPin } from "lucide-react";
import { useSettings } from "@/lib/hooks";

const SOCIAL_ICONS = { youtube: Youtube, instagram: Instagram, facebook: Facebook, linkedin: Linkedin, twitter: Twitter, telegram: Send };

export default function Footer() {
  const { data: s } = useSettings();
  const social = s?.social || {};
  const activeSocial = Object.entries(social).filter(([, v]) => v && v.enabled && v.url);
  const wa = (s?.whatsapp_number || "").replace(/[^0-9]/g, "");
  return (
    <footer className="bg-navy text-slate-300 mt-16" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center gap-2 mb-3">
            {s?.logo_url ? (
              <img src={s.logo_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${s.logo_url}` : s.logo_url} alt={s?.business_name || "Water Business Guru"} className="h-9 w-auto object-contain" data-testid="footer-logo" />
            ) : (
              <span className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                <Droplets className="w-4 h-4 text-teal-500" />
              </span>
            )}
            <div className="leading-tight">
              <span className="block font-heading font-bold text-white">{s?.business_name || "Water Business Guru"}</span>
              {s?.founder_name && <span className="block text-[11px] text-slate-400">By {s.founder_name}</span>}
            </div>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">
            {s?.footer_note || "Practical business systems for RO / water purifier service professionals."}
          </p>
          {activeSocial.length > 0 && (
            <div className="flex items-center gap-3 mt-4" data-testid="footer-social">
              {activeSocial.map(([plat, v]) => {
                const Icon = SOCIAL_ICONS[plat] || Send;
                return (
                  <a key={plat} href={v.url} target="_blank" rel="noopener noreferrer nofollow"
                     aria-label={plat} className="text-slate-400 hover:text-teal transition-colors" data-testid={`social-${plat}`}>
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          )}
        </div>
        <div>
          <h4 className="text-white font-semibold text-sm mb-3">Shop</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/shop" className="hover:text-teal">All Products</Link></li>
            <li><Link to="/shop?type=DIGITAL" className="hover:text-teal">Digital Products</Link></li>
            <li><Link to="/product/complete-combo" className="hover:text-teal">Complete Combo</Link></li>
            <li><Link to="/upcoming" className="hover:text-teal">Upcoming</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold text-sm mb-3">Company</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-teal">About Us</Link></li>
            <li><Link to="/contact" className="hover:text-teal">Contact Us</Link></li>
            <li><Link to="/faq" className="hover:text-teal">FAQ</Link></li>
            <li><Link to="/grievance" className="hover:text-teal">Grievance &amp; Support</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold text-sm mb-3">Legal &amp; Policies</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/terms" className="hover:text-teal">Terms &amp; Conditions</Link></li>
            <li><Link to="/privacy" className="hover:text-teal">Privacy Policy</Link></li>
            <li><Link to="/refund-policy" className="hover:text-teal">Refund &amp; Cancellation</Link></li>
            <li><Link to="/shipping-policy" className="hover:text-teal">Shipping &amp; Delivery</Link></li>
            <li><Link to="/digital-delivery" className="hover:text-teal">Digital Delivery</Link></li>
            <li><Link to="/disclaimer" className="hover:text-teal">Disclaimer</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-400" data-testid="footer-contact">
          {s?.support_email && <a href={`mailto:${s.support_email}`} className="inline-flex items-center gap-1.5 hover:text-teal"><Mail className="w-3.5 h-3.5" /> {s.support_email}</a>}
          {s?.support_phone && <a href={`tel:${s.support_phone.replace(/[^0-9+]/g, "")}`} className="inline-flex items-center gap-1.5 hover:text-teal"><Phone className="w-3.5 h-3.5" /> {s.support_phone}</a>}
          {wa && <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-teal" data-testid="footer-whatsapp"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</a>}
          {[s?.address, s?.city, s?.state, s?.pin_code, s?.country].filter(Boolean).length > 0 &&
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {[s?.address, s?.city, s?.state, s?.pin_code, s?.country].filter(Boolean).join(", ")}</span>}
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row justify-between gap-2 text-xs text-slate-500">
          <span>© {new Date().getFullYear()} {s?.legal_name || s?.business_name || "Water Business Guru"}. All rights reserved.</span>
          <span>{s?.founder_name ? `By ${s.founder_name}` : ""}</span>
        </div>
      </div>
    </footer>
  );
}

